/**
 * Правка полей доставки сделки Bitrix по одному направлению: статус, адрес, «доставить / забрать не
 * позднее» у клиента и у салона, курьер (служба или ссылка на заказ), его телефон, комментарий.
 * Общая для «Доставок» и вкладки «Доставка» «Чатов»; пишет `POST /api/delivery/{сделка}/{start|end}`
 * (**боевой Bitrix и договор CRM**). Времена — московские, как в Bitrix.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { DateTimePick } from "@/components/DateTimePick";
import {
  CONTRACT_TEXT,
  contractFailed,
  type DeliveryEdit,
  type DeliveryNow,
  type DeliveryResult,
  type DeliveryStatus,
  type Direction,
  saveDelivery,
} from "@/lib/delivery";

const input =
  "h-8 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg placeholder:text-fg-faint";
const btn =
  "inline-flex h-7 items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg-muted hover:bg-bg-hover disabled:opacity-50";
const btnMain =
  "inline-flex h-8 items-center gap-1 rounded-[var(--radius-ui)] bg-accent px-3 text-[12.5px] font-medium text-accent-text hover:opacity-90 disabled:opacity-50";

export const TITLE: Record<Direction, string> = {
  start: "Доставить клиенту",
  end: "Забрать у клиента",
};
const NEED: Record<Direction, string> = {
  start: "Нужна доставка",
  end: "Нужен забор",
};
const TIME: Record<Direction, string> = {
  start: "Доставить клиенту",
  end: "Забрать у клиента",
};
export const TIME_SALON: Record<Direction, string> = {
  start: "Забрать из салона",
  end: "Вернуть в салон",
};

/** Поля формы; времена — `ГГГГ-ММ-ДДTЧЧ:ММ` или пусто. */
export type Form = {
  need: boolean;
  status: string;
  address: string;
  time: string;
  time_salon: string;
  courier: string;
  courier_phone: string;
  comment: string;
};

export const formOfNow = (n: DeliveryNow | undefined, cancel: string): Form => ({
  need: Boolean(n && n.status !== cancel && (n.status || n.address)),
  status: n?.status ?? "",
  address: n?.address ?? "",
  time: (n?.time ?? "").replace(" ", "T"),
  time_salon: (n?.time_salon ?? "").replace(" ", "T"),
  courier: n?.courier ?? "",
  courier_phone: n?.courier_phone ?? "",
  comment: n?.comment ?? "",
});

/** Только изменённое: сервер не трогает поля, которых нет в запросе. */
function diff(was: Form, now: Form): DeliveryEdit {
  const out: DeliveryEdit = {};
  for (const k of [
    "address",
    "time",
    "time_salon",
    "courier",
    "courier_phone",
    "comment",
  ] as const)
    if (was[k].trim() !== now[k].trim()) out[k] = now[k].trim();
  // Явно выбранный статус сильнее признака; «не нужна» — всегда признаком (сервер поставит «отменили»).
  if (now.need && now.status !== was.status) out.status = now.status;
  else if (now.need !== was.need) out.need = now.need;
  return out;
}

export function ResultLine({ r }: { r: DeliveryResult }) {
  return (
    <div className="text-[12px]" data-annot="итог записи">
      <span className="text-ok">
        Bitrix: {r.bitrix === "ok" ? "записано" : r.bitrix === "pending" ? "не ответил — дошлём сами" : "без изменений"}
      </span>
      <span className="text-fg-faint"> · </span>
      <span
        className={
          contractFailed(r.contract) ? "font-medium text-err" : "text-fg-muted"
        }
      >
        {CONTRACT_TEXT[r.contract]}
      </span>
    </div>
  );
}

export function EditForm({
  deal,
  direction,
  initial,
  statuses,
  onSaved,
  onCancel,
}: {
  deal: number;
  direction: Direction;
  initial: Form;
  statuses: DeliveryStatus[];
  onSaved: (r: DeliveryResult) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState<Form>(initial);
  const [failure, setFailure] = useState("");
  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setF((x) => ({ ...x, [k]: v }));
  const m = useMutation({
    mutationFn: (body: DeliveryEdit) => saveDelivery(deal, direction, body),
    onSuccess: onSaved,
    onError: (e: Error) => setFailure(e.message),
  });
  const body = diff(initial, f);
  const changed = Object.keys(body).length > 0;
  // DateTimePick отдаёт день без времени как полночь — для курьера это не время.
  const noHour = [f.time, f.time_salon].some((t) => t.endsWith("T00:00"));
  const work = statuses.filter((s) => s.kind !== "cancel");
  return (
    <form
      className="mt-2 grid gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-3"
      data-annot={`правка: ${TITLE[direction].toLowerCase()}, сделка ${deal}`}
      onSubmit={(e) => {
        e.preventDefault();
        setFailure("");
        if (changed && !noHour) m.mutate(body);
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5 text-[12.5px] text-fg">
          <input
            type="checkbox"
            checked={f.need}
            onChange={(e) => set("need", e.target.checked)}
          />
          {NEED[direction]}
        </label>
        <select
          className={`${input} !w-auto`}
          value={f.need ? f.status : ""}
          disabled={!f.need}
          onChange={(e) => set("status", e.target.value)}
          aria-label="Статус"
        >
          <option value="">{f.need ? "статус не задан" : "не нужна"}</option>
          {work.map((s) => (
            <option key={s.code} value={s.code}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <input
        className={input}
        value={f.address}
        maxLength={255}
        placeholder="Адрес: улица, дом, подъезд, этаж, квартира, домофон"
        onChange={(e) => set("address", e.target.value)}
        aria-label="Адрес"
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <DateTimePick
          label={TIME[direction]}
          value={f.time}
          onChange={(v) => set("time", v)}
        />
        <DateTimePick
          label={TIME_SALON[direction]}
          value={f.time_salon}
          onChange={(v) => set("time_salon", v)}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          className={input}
          value={f.courier}
          maxLength={255}
          placeholder="Служба или ссылка на заказ: Достависта, Яндекс…"
          onChange={(e) => set("courier", e.target.value)}
          aria-label="Курьер"
        />
        <input
          className={input}
          value={f.courier_phone}
          maxLength={128}
          placeholder="Телефон курьера"
          onChange={(e) => set("courier_phone", e.target.value)}
          aria-label="Телефон курьера"
        />
      </div>
      <input
        className={input}
        value={f.comment}
        maxLength={512}
        placeholder="Комментарий курьеру (один на сделку)"
        onChange={(e) => set("comment", e.target.value)}
        aria-label="Комментарий курьеру"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          className={btnMain}
          disabled={!changed || noHour || m.isPending}
        >
          {m.isPending ? "Записываю…" : "Сохранить"}
        </button>
        <button type="button" className={btn} onClick={onCancel}>
          Отмена
        </button>
        <span className="text-[11.5px] text-fg-faint">
          время — московское; запись идёт в Bitrix и в договор CRM
        </span>
      </div>
      {noHour && (
        <div className="text-[12px] text-err">
          Наберите время: день без часа курьеру не подходит.
        </div>
      )}
      {failure && (
        <div className="text-[12px] text-err">Не записано: {failure}</div>
      )}
    </form>
  );
}
